import { fetchCompanyPlan } from "./../company/company-actions";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { action } from "typesafe-actions";
import { CandidateQuery } from "../../api/talent/models/candidates-query.model";
import CandidateAction from "../../models/candidate-action.model";
import { PlanType } from "../../models/plan-type.enum";
import { CompanyActionTypes } from "../company/company-types";
import { ecosystemApi } from "./../../api/talent/ecosystem-services";
import { talentApi } from "./../../api/talent/talent-service";
import { AppState } from "./../app-state";
import { CandidateActionTypes } from "./candidate-types";
import { JobOfferPayload } from "../../models/candidate-action.model";
import { getCurrentPlan } from "../plan/plan-actions";
import { checkTriggerNps } from "../nps/nps-actions";

export interface UnlockCandidate {
  talentId: string;
  trackId?: string;
  metadata?: {
    job_offer: JobOfferPayload;
  };
}

export const clearCandidateDetail = () =>
  action(CandidateActionTypes.CLEAR_CANDIDATE_DETAIL);

export const fetchCandidate = (
  talentId: string,
  trackId: string,
  ecosystem?: string,
  currencyIso?: string
) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.FETCH_CANDIDATE));
    try {
      const response = await talentApi.fetchTalent(
        talentId,
        trackId,
        ecosystem,
        currencyIso
      );
      dispatch(
        action(CandidateActionTypes.FETCH_CANDIDATE_SUCCESS, response.data)
      );
    } catch (error) {
      dispatch(action(CandidateActionTypes.FETCH_CANDIDATE_ERROR, error));
    }
  };
};

export const notifyOpenCandidateDetail = (
  talentId: string,
  trackId: string
) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.NOTIFY_OPEN_CANDIDATE_DETAIL));

    try {
      const payload: CandidateAction = { action: "open" };
      await talentApi.sendAction(payload, talentId, trackId);
    } catch (error) {}
  };
};

export const checkPermissionToUnlockCandidateVertical = () => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.UNLOCK_CANDIDATE));

    const response = await talentApi.fetchTalentPlan();

    // Updating company plan
    dispatch(
      action(CompanyActionTypes.FETCH_COMPANY_PLAN_SUCCESS, response.data)
    );

    return dispatch(
      action(CandidateActionTypes.UNLOCK_CANDIDATE_CONFIRM_NOTIFY, true)
    );
  };
};

export const checkPermissionToUnlockCandidate = (ecosystem?: string) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.UNLOCK_CANDIDATE));

    const response = await talentApi.fetchTalentPlan();

    // Updating company plan
    dispatch(
      action(CompanyActionTypes.FETCH_COMPANY_PLAN_SUCCESS, response.data)
    );

    const planData = response.data;
    const isSponsor = planData.plan === PlanType.SPONSOR;

    if (!isSponsor) {
      const isBasic = planData.plan === PlanType.BASIC;

      if (isBasic) {
        return dispatch(
          action(CandidateActionTypes.UNLOCK_CANDIDATE_NEED_PERMISSION, true)
        );
      }
    }

    return dispatch(
      action(CandidateActionTypes.UNLOCK_CANDIDATE_CONFIRM_NOTIFY, true)
    );
  };
};

export const confirmPermissionToUnlockCandidate = (confirm: boolean) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(
      action(
        CandidateActionTypes.UNLOCK_CANDIDATE_CONFIRM_PERMISSION_TO_UNLOCK,
        confirm
      )
    );
  };
};

export const confirmLeedRequest = (confirm: boolean) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(
      action(
        CandidateActionTypes.UNLOCK_CANDIDATE_CONFIRM_PERMISSION_TO_UNLOCK,
        confirm
      )
    );
  };
};

export const confirmEcosystemAuthorizationRequest = (confirm: boolean) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(
      action(
        CandidateActionTypes.UNLOCK_CANDIDATE_CONFIRM_PERMISSION_TO_UNLOCK,
        confirm
      )
    );
  };
};

export const confirmNotifyToUnlockCandidate = () => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(
      action(CandidateActionTypes.UNLOCK_CANDIDATE_CONFIRM_NOTIFY, false)
    );
  };
};

export const createJobOffer = (jobOffer: JobOfferPayload) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.JOB_OFFER, jobOffer));
  };
};

export const unlockCandidate = ({
  talentId,
  trackId,
  metadata,
}: UnlockCandidate) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.UNLOCK_CANDIDATE));
    try {
      const payload: CandidateAction = { action: "interview" };

      if (metadata) {
        payload["metadata"] = metadata;
      }
      const response = await talentApi.sendAction(payload, talentId, trackId);

      dispatch(
        action(CandidateActionTypes.UNLOCK_CANDIDATE_SUCCESS, response.data)
      );

      // Updating leed
      dispatch(fetchCompanyPlan());
      dispatch(getCurrentPlan());
    } catch (error) {
      dispatch(action(CandidateActionTypes.UNLOCK_CANDIDATE_ERROR, error));
    }
  };
};

export const declineCandidate = (
  talentId: string,
  trackId: string,
  reasonId: string
) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.DECLINE_CANDIDATE));

    try {
      const payload: CandidateAction = {
        action: "not_approved",
        reason: reasonId,
      };
      const response = await talentApi.sendAction(payload, talentId, trackId);
      dispatch(
        action(CandidateActionTypes.DECLINE_CANDIDATE_SUCCESS, response.data)
      );
      dispatch(checkTriggerNps());
    } catch (error) {
      dispatch(action(CandidateActionTypes.DECLINE_CANDIDATE_ERROR, error));
    }
  };
};

export const candidateNotInterviewed = (
  talentId: string,
  trackId: string,
  reasonId: string
) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.CANDIDATE_NOT_INTERVIEWED));

    try {
      const payload: CandidateAction = {
        action: "not_interviewed",
        reason: reasonId,
      };
      const response = await talentApi.sendAction(payload, talentId, trackId);
      dispatch(
        action(
          CandidateActionTypes.CANDIDATE_NOT_INTERVIEWED_SUCCESS,
          response.data
        )
      );
      dispatch(checkTriggerNps());
    } catch (error) {
      dispatch(
        action(CandidateActionTypes.CANDIDATE_NOT_INTERVIEWED_ERROR, error)
      );
    }
  };
};

export const candidateHired = (talentId: string, trackId: string) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.CANDIDATE_HIRED));

    try {
      const payload: CandidateAction = { action: "hired" };
      const response = await talentApi.sendAction(payload, talentId, trackId);
      dispatch(
        action(CandidateActionTypes.CANDIDATE_HIRED_SUCCESS, response.data)
      );
      dispatch(checkTriggerNps());
    } catch (error) {
      dispatch(action(CandidateActionTypes.CANDIDATE_HIRED_ERROR, error));
    }
  };
};

export const denyJobOffer = (
  talentId: string,
  trackId: string,
  reasonId: string
) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.DENY_JOB_OFFER));

    try {
      const payload: CandidateAction = {
        action: "deny_job_offer",
        reason: reasonId,
      };
      const response = await talentApi.sendAction(payload, talentId, trackId);
      dispatch(
        action(CandidateActionTypes.DENY_JOB_OFFER_SUCCESS, response.data)
      );
      dispatch(checkTriggerNps());
    } catch (error) {
      dispatch(action(CandidateActionTypes.DENY_JOB_OFFER_ERROR, error));
    }
  };
};

export const fetchDeclineCandidateReasons = () => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.FETCH_DECLINE_CANDIDATE_REASONS));

    try {
      const response = await talentApi.fetchDeclineCandidateReasons();
      dispatch(
        action(
          CandidateActionTypes.FETCH_DECLINE_CANDIDATE_REASONS_SUCCESS,
          response.data
        )
      );
    } catch (error) {
      dispatch(
        action(
          CandidateActionTypes.FETCH_DECLINE_CANDIDATE_REASONS_ERROR,
          error
        )
      );
    }
  };
};

export const fetchCandidateEcosystems = () => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.FETCH_CANDIDATE_ECOSYSTEMS));

    try {
      const response = await ecosystemApi.fetchEcosystemsData();
      dispatch(
        action(
          CandidateActionTypes.FETCH_CANDIDATE_ECOSYSTEMS_SUCCESS,
          response.data
        )
      );
    } catch (error) {
      dispatch(
        action(CandidateActionTypes.FETCH_CANDIDATE_ECOSYSTEMS_ERROR, error)
      );
    }
  };
};

export const fetchCandidateCareer = () => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.FETCH_CANDIDATE_CAREER));
    try {
      const response = await ecosystemApi.fetchCareerData();
      dispatch(
        action(
          CandidateActionTypes.FETCH_CANDIDATE_CAREER_SUCCESS,
          response.data
        )
      );
      dispatch(
        action(
          CandidateActionTypes.FETCH_CANDIDATE_ECOSYSTEMS_SUCCESS,
          response.data
            .flatMap((career) => career.ecosystems)
            .sort((e1, e2) => e1.name.localeCompare(e2.name))
        )
      );
    } catch (error) {
      dispatch(
        action(CandidateActionTypes.FETCH_CANDIDATE_CAREER_ERROR, error)
      );
    }
  };
};

export const fetchCandidatePool = (
  ecosystemId: string,
  query: CandidateQuery
) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.FETCH_CANDIDATE_POOL));
    try {
      const response = await talentApi.fetchCandidatePool(ecosystemId, query);
      dispatch(
        action(CandidateActionTypes.FETCH_CANDIDATE_POOL_SUCCESS, response)
      );
    } catch (error) {
      dispatch(action(CandidateActionTypes.FETCH_CANDIDATE_POOL_ERROR, error));
    }
  };
};

export const fetchCandidateOfRanking = (
  main_skill: string,
  query: CandidateQuery
) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.FETCH_CANDIDATE_OF_RANKING));
    try {
      const response = await talentApi.fetchCandidateOfRanking(main_skill, query);
      dispatch(
        action(CandidateActionTypes.FETCH_CANDIDATE_OF_RANKING_SUCCESS, response.data)
      );
    } catch (error) {
      dispatch(action(CandidateActionTypes.FETCH_CANDIDATE_OF_RANKING_ERROR, error));
    }
  };
}

export const fetchCandidateDetailOfRanking = (
  talentId: string
) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.FETCH_CANDIDATE_DETAIL_OF_RANKING));
    try {
      const response = await talentApi.fetchCandidateDetailOfRanking(talentId);
      dispatch(
        action(CandidateActionTypes.FETCH_CANDIDATE_DETAIL_OF_RANKING_SUCCESS, response.data)
      );
    } catch (error) {
      dispatch(action(CandidateActionTypes.FETCH_CANDIDATE_DETAIL_OF_RANKING_ERROR, error));
    }
  };
}

export const clearCandidates = () => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.CLEAR_CANDIDATES));
  };
};

export const fetchSkillsOfCandidatePool = (
  ecosystemId: string,
  query: CandidateQuery
) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.FETCH_CANDIDATE_POOL_SKILLS));
    try {
      const response = await talentApi.fetchSkillsOfCandidatePool(
        ecosystemId,
        query
      );
      const sortedData = response.data.sort();
      dispatch(
        action(
          CandidateActionTypes.FETCH_CANDIDATE_POOL_SUCCESS_SKILLS,
          sortedData
        )
      );
    } catch (error) {
      dispatch(
        action(CandidateActionTypes.FETCH_CANDIDATE_POOL_SKILLS_ERROR, error)
      );
    }
  };
};

export const addCandidateTag = (tagsId: string[], candidateId: string) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.ADD_CANDIDATE_TAG));
    try {
      const response = await talentApi.addCandidateTag(tagsId, candidateId);
      dispatch(
        action(CandidateActionTypes.ADD_CANDIDATE_TAG_SUCCESS, response.data)
      );
    } catch (error) {
      dispatch(action(CandidateActionTypes.ADD_CANDIDATE_TAG_ERROR, error));
    }
  };
};

export const removeCandidateTag = (tagId: string, candidateId: string) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.REMOVE_CANDIDATE_TAG));
    try {
      await talentApi.removeCandidateTag(tagId, candidateId);
      dispatch(
        action(CandidateActionTypes.REMOVE_CANDIDATE_TAG_SUCCESS, {})
      );
    } catch (error) {
      dispatch(action(CandidateActionTypes.REMOVE_CANDIDATE_TAG_ERROR, error));
    }
  };
};

export const createTag = (name: string, color: string) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.CREATE_TAG));
    try {
      const response = await talentApi.createTag(name, color);
      dispatch(action(CandidateActionTypes.CREATE_TAG_SUCCESS, response.data));
    } catch (error) {
      dispatch(action(CandidateActionTypes.CREATE_TAG_ERROR, error));
    }
  };
};

export const fetchTags = (name: string) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.FETCH_TAGS));
    try {
      const response = await talentApi.fetchTags(name);
      dispatch(action(CandidateActionTypes.FETCH_TAGS_SUCCESS, response.data));
    } catch (error) {
      dispatch(action(CandidateActionTypes.FETCH_TAGS_ERROR, error));
    }
  };
}

export const fetchTagsById = (ids: string[]) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.FETCH_TAGS_BY_ID));
    try {
      const response = await talentApi.fetchTagsByIds(ids);
      dispatch(action(CandidateActionTypes.FETCH_TAGS_BY_ID_SUCCESS, response.data));
    } catch (error) {
      dispatch(action(CandidateActionTypes.FETCH_TAGS_BY_ID_ERROR, error));
    }
  }
}

export const setCandidateNotAfordable = (
  talentId: string,
  trackId: string,
  jobSetupId: string,
) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.SET_CANDIDATE_NOT_AFORDABLE));
    try {
      const response = await talentApi.setCandidateNotAfordable(talentId, trackId, jobSetupId);
      dispatch(action(CandidateActionTypes.SET_CANDIDATE_NOT_AFORDABLE_SUCCESS, response.data));
    } catch (error) {
      dispatch(action(CandidateActionTypes.SET_CANDIDATE_NOT_AFORDABLE_ERROR, error));
    }
  }
}

export const setCandidateToAffordable = (
  talentId: string,
  trackId: string,
) => {
  return async (dispatch: ThunkDispatch<AppState, void, Action>) => {
    dispatch(action(CandidateActionTypes.SET_CANDIDATE_TO_AFFORDABLE));
    try {
      const response = await talentApi.setCandidateToAffordable(talentId, trackId);
      dispatch(action(CandidateActionTypes.SET_CANDIDATE_TO_AFFORDABLE_SUCCESS, response.data));
    } catch (error) {
      dispatch(action(CandidateActionTypes.SET_CANDIDATE_TO_AFFORDABLE_ERROR, error));
    }
  }
}

